import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import DatePicker from "react-datepicker";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Snackbar,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Close as CloseIcon, Search } from "@mui/icons-material";
import moment from "moment";

import {
  fetchReportingProduct,
  getListProductReporting,
} from "../../../../services/ReportingApi";
import { getMultipleOutletOption } from "../../../../services/importProductApi";
import useDebounce from "../../../hooks/useDebounce";
import LoadingBackdrop from "../../../common/particles.jsx/LoadingBackdrop";
import ReportingProductDetailModal from "./ReportingProductDetailModal";
import PagePagination from "../../../common/particles.jsx/PagePagination";

const toDate = (date) => moment(date).format("YYYY-MM-DD");

const ReportingProductList = (props) => {
  const { FETCH_LIMIT, period, isSell } = props;
  const [reqBody, setReqBody] = useState({
    limit: FETCH_LIMIT,
    offset: 1,
    company_id: [],
    payment_method: 0,
    period: 0,
    start_date: "",
    end_date: "",
    product_name: "",
    type: isSell, // true penjualan, false pembelian
  });
  const [param, setParam] = useState({
    limit: FETCH_LIMIT,
    offset: 1,
    name: "",
    type: isSell, // true penjualan, false pembelian
    company_id: [],
    period: 0,
    start_date: "",
    end_date: "",
    payment_method: 0, //0.all, 1. offline, 2.online
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(null);
  const [periodBuffer, setPeriodBuffer] = useState(0);

  const handleXlsxData = (xlsxData) => {
    const url = xlsxData;
    const a = document.createElement("a");
    a.download = `reporting_product_${isSell ? "penjualan" : "pembelian"}.xlsx`;
    a.href = url;

    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const {
    mutate: mutateReportingProduct,
    data: reportingProduct,
    isLoading: isReportingProductLoading,
  } = useMutation({
    mutationFn: fetchReportingProduct,
    onError: (err) => setErrorMessage(err.message.id),
  });

  const {
    mutate: mutateExportReportingProduct,
    isLoading: isExportingExcelLoading,
  } = useMutation({
    mutationFn: fetchReportingProduct,
    onSuccess: (data) => handleXlsxData(data.excel_url),
  });

  const debounce = useDebounce(param.name, 500);

  const {
    data: listProductReporting,
    isLoading: isListProductReportingLoading,
    mutate: mutateGetListProduct,
  } = useMutation({
    mutationFn: getListProductReporting,
  });

  const { data: multipleOutletOption } = useQuery({
    queryKey: ["outlet-option", param],
    queryFn: () => getMultipleOutletOption(),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (
      reqBody.payment_method !== -1 &&
      reqBody.company_id.length > 0 &&
      periodBuffer >= 0 &&
      reqBody.product_name.length !== 0 &&
      openDetailModal !== null
    ) {
      mutateReportingProduct({
        ...reqBody,
        company_id: reqBody.company_id.map((data) => data.company_id),
        start_date:
          toDate(reqBody.start_date) !== "Invalid date"
            ? toDate(reqBody.start_date)
            : "",
        end_date:
          toDate(reqBody.end_date) !== "Invalid date"
            ? toDate(reqBody.end_date)
            : "",
      });
    }
  }, [reqBody, mutateReportingProduct, periodBuffer, openDetailModal, isSell]);

  useEffect(() => {
    if (reqBody.company_id.length > 0) {
      mutateGetListProduct({
        ...param,
        period: param.period === -2 ? 9 : param.period,
        company_id: reqBody.company_id.map((data) => data.company_id),
      });
    }
  }, [param, reqBody.company_id, debounce, mutateGetListProduct, isSell]);

  useEffect(() => {
    if (multipleOutletOption)
      setReqBody((prevValue) => ({
        ...prevValue,
        company_id: multipleOutletOption?.map((data) => ({
          company_id: data.company_id,
          company_name: data.company_name,
        })),
      }));
  }, [multipleOutletOption, isSell]);

  useEffect(() => {
    setReqBody({
      limit: FETCH_LIMIT,
      offset: 1,
      company_id: [],
      payment_method: 0,
      period: 0,
      start_date: "",
      end_date: "",
      product_name: "",
      type: isSell, // true penjualan, false pembelian})
    });
    setParam({
      limit: FETCH_LIMIT,
      offset: 1,
      name: "",
      type: isSell, // true penjualan, false pembelian
      company_id: [],
      period: 0,
      start_date: "",
      end_date: "",
      payment_method: 0, //0.all, 1. offline, 2.online
    });
  }, [isSell]);

  return (
    <div>
      <LoadingBackdrop isLoading={isExportingExcelLoading} />
      <div className="flex gap-2 items-center my-4">
        <FormControl sx={{ width: "100%" }}>
          <InputLabel id="demo-simple-select-label">Outlet</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            label="FOutlet"
            value={reqBody.company_id}
            onChange={(e) => {
              if (e.target.value === 0) {
                setReqBody((prevValue) => ({
                  ...prevValue,
                  company_id: multipleOutletOption?.map((data) => ({
                    company_id: data.company_id,
                    company_name: data.company_name,
                  })),
                }));
              } else {
                setReqBody((prevValue) => {
                  if (
                    prevValue.company_id
                      .map((data) => data.company_id)
                      .some((data) => data === e.target.value.company_id)
                  ) {
                    return {
                      ...prevValue,
                      company_id: prevValue.company_id.filter(
                        (data) => data.company_id === e.target.value.company_id
                      ),
                    };
                  } else {
                    return {
                      ...prevValue,
                      company_id: [...prevValue.company_id, e.target.value],
                    };
                  }
                });
              }
            }}
            renderValue={(selected) =>
              selected.map((data) => data.company_name).join(", ")
            }
          >
            <MenuItem value={0}>Semua Outlet</MenuItem>
            {multipleOutletOption && multipleOutletOption !== undefined ? (
              multipleOutletOption.map((data, index) => (
                <MenuItem
                  key={index}
                  value={{
                    company_id: data.company_id,
                    company_name: data.company_name,
                  }}
                >
                  {data.company_name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={0} sx={{ display: "none" }} disabled></MenuItem>
            )}
          </Select>
        </FormControl>
        <FormControl sx={{ width: "100%" }}>
          <InputLabel id="demo-simple-select-label">
            Metode Penjualan
          </InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            label="Metode Penjualan"
            value={reqBody.payment_method}
            onChange={(e) => {
              setReqBody((prevValue) => ({
                ...prevValue,
                payment_method: e.target.value,
              }));
              setParam((prevValue) => ({
                ...prevValue,
                payment_method: e.target.value,
              }));
            }}
          >
            <MenuItem value={0}>Semua</MenuItem>
            <MenuItem value={1}>Penjualan Offline</MenuItem>
            <MenuItem value={2}>Penjualan Online</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ width: "100%" }}>
          <InputLabel id="demo-simple-select-label">Periode</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            label="Periode"
            value={periodBuffer}
            onChange={(e) => {
              if (e.target.value === 10 || e.target.value === 9) {
                return;
              }
              setPeriodBuffer(e.target.value);
              setReqBody((prevValue) => ({
                ...prevValue,
                period: e.target.value,
                start_date: "",
                end_date: "",
              }));
              setParam((prevValue) => ({
                ...prevValue,
                period: e.target.value,
                start_date: "",
                end_date: "",
              }));
            }}
          >
            <MenuItem value={10} disabled sx={{ display: "none" }}>
              {toDate(reqBody.start_date) + " - " + toDate(reqBody.end_date)}
            </MenuItem>
            {period.map((data, index) => (
              <MenuItem
                value={data.value}
                key={index}
                onClick={() => {
                  if (data.value === -2) {
                    setIsDateModalOpen(true);
                  }
                }}
              >
                {data.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <TextField
        fullWidth
        sx={{ marginBottom: 2 }}
        placeholder="Cari Produk"
        onChange={(e) =>
          setParam((prevValue) => ({
            ...prevValue,
            name: e.target.value,
          }))
        }
        value={param.name}
        InputProps={{
          startAdornment: (
            <InputAdornment>
              <Search />
            </InputAdornment>
          ),
        }}
      />

      <div className="flex w-full mb-8">
        <div className="w-full">
          <div className="block overflow-x-auto w-full rounded-md">
            <table className="w-full overflow-x-auto rounded-md border-collapse">
              <thead className="bg-blue-500 text-white">
                <th className="p-2 text-center w-[50px]">No</th>
                <th className="p-2 text-left whitespace-nowrap">Nama Produk</th>
                <th className="p-2 text-center">Kuantitas</th>
              </thead>
              <tbody>
                {listProductReporting &&
                !isListProductReportingLoading &&
                listProductReporting.data ? (
                  listProductReporting.data.map((item, i) => (
                    <tr className="border-2">
                      <td className="p-2 text-center w-[50px]">
                        {(param.offset - 1) * param.limit + (i + 1)}
                      </td>
                      <td
                        className="p-2 text-left text-blue-500 hover:underline cursor-pointer"
                        onClick={() => {
                          setOpenDetailModal(item);
                          setReqBody((prevValue) => ({
                            ...prevValue,
                            product_name: item.product_name,
                          }));
                        }}
                      >
                        {item.product_name}
                      </td>
                      <td className="p-2 text-center">{item.quantity}</td>
                    </tr>
                  ))
                ) : (
                  <tr className="border-2">
                    <td colSpan={2} className="p-2 text-center">
                      {isListProductReportingLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        "Tidak ada produk"
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {listProductReporting &&
      listProductReporting.count > 0 &&
      listProductReporting.data !== null ? (
        <>
          <div className="mb-4">
            <PagePagination
              offset={param.offset}
              setOffset={(e) =>
                setParam((prevValue) => ({ ...prevValue, offset: e }))
              }
              limit={param.limit}
              setLimit={(e) => {
                setParam((prevValue) => ({ ...prevValue, limit: e }));
              }}
              total={listProductReporting.count}
            />
          </div>
        </>
      ) : null}

      <ReportingProductDetailModal
        open={openDetailModal}
        setOpen={setOpenDetailModal}
        isReportingProductLoading={isReportingProductLoading}
        reportingProduct={reportingProduct}
        reqBody={reqBody}
        setReqBody={setReqBody}
        FETCH_LIMIT={FETCH_LIMIT}
        disableExport={
          isExportingExcelLoading ||
          reportingProduct === undefined ||
          reportingProduct?.count === 0 ||
          isReportingProductLoading
        }
        handleExport={() => {
          if (
            reqBody.payment_method !== -1 &&
            reqBody.company_id.length > 0 &&
            reqBody.period >= 0 &&
            reqBody.product_name.length !== 0
          ) {
            mutateExportReportingProduct({
              ...reqBody,
              company_id: reqBody.company_id.map((data) => data.company_id),
              limit: 0,
              offset: 0,
            });
          }
        }}
      />

      <Dialog
        open={isDateModalOpen}
        onClose={() => {
          setIsDateModalOpen(false);
        }}
      >
        <DialogTitle>Tanggal</DialogTitle>
        <DialogContent>
          <div className="flex">
            <DatePicker
              selected={reqBody.start_date}
              onChange={(dates) => {
                const [start, end] = dates;
                setReqBody((prevValue) => ({
                  ...prevValue,
                  start_date: start,
                  end_date: end,
                }));
                setParam((prevValue) => ({
                  ...prevValue,
                  start_date: moment(start).format("YYYY-MM-DD"),
                  end_date: moment(end).format("YYYY-MM-DD"),
                }));
              }}
              startDate={reqBody.start_date}
              endDate={reqBody.end_date}
              selectsRange
              inline
            />
          </div>
          <button
            className="bg-indigo-500 rounded text-white px-2 py-1 font-semibold"
            onClick={() => {
              setIsDateModalOpen(false);
              setPeriodBuffer(10);
              setReqBody((prevValue) => ({
                ...prevValue,
                period: 9,
                start_date: reqBody.start_date,
                end_date: reqBody.end_date,
              }));
              setParam((prevValue) => ({
                ...prevValue,
                start_date: param.start_date,
                end_date: param.end_date,
              }));
            }}
          >
            ok
          </button>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={5000}
        onClose={() => setErrorMessage("")}
        message={errorMessage}
        action={
          <>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={(_, reason) => {
                if (reason === "clickaway") {
                  return;
                }
                setErrorMessage("");
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      />
    </div>
  );
};

export default ReportingProductList;
